import { Button, Input, Label } from "@/components/ui";
import { centsToUsd } from "@/lib/money";
import { draftToLineItemInput } from "./quoteMath";
import type { LineItemDraft } from "@/api/types/quote";

interface LineItemsTableProps {
  items: LineItemDraft[];
  onChange: (items: LineItemDraft[]) => void;
  onAdd: () => void;
  disabled?: boolean;
}

/**
 * Editable line-item table: label, qty, unit price (dollars). Amount is
 * computed read-only from qty * unit price — never entered directly, so it
 * can never drift from the row it belongs to.
 */
export function LineItemsTable({ items, onChange, onAdd, disabled }: LineItemsTableProps) {
  function updateRow(key: string, patch: Partial<LineItemDraft>) {
    onChange(items.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function removeRow(key: string) {
    onChange(items.filter((row) => row.key !== key));
  }

  return (
    <div className="space-y-2">
      <div className="hidden grid-cols-[1fr_80px_120px_120px_40px] gap-2 px-1 sm:grid">
        <Label>Label</Label>
        <Label>Qty</Label>
        <Label>Unit price</Label>
        <Label>Amount</Label>
        <span />
      </div>

      {items.map((row) => {
        const amount = draftToLineItemInput(row).amount_cents;
        return (
          <div
            key={row.key}
            className="grid grid-cols-2 items-center gap-2 rounded-md border border-border p-2 sm:grid-cols-[1fr_80px_120px_120px_40px] sm:border-0 sm:p-0"
          >
            <Input
              aria-label="Line item label"
              value={row.label}
              placeholder="Line item"
              disabled={disabled}
              onChange={(e) => updateRow(row.key, { label: e.target.value })}
              className="col-span-2 sm:col-span-1"
            />
            <Input
              aria-label="Line item quantity"
              type="number"
              min={0}
              step="1"
              value={row.qty}
              disabled={disabled}
              onChange={(e) => updateRow(row.key, { qty: e.target.value })}
            />
            <Input
              aria-label="Line item unit price"
              inputMode="decimal"
              placeholder="0.00"
              value={row.unitDollars}
              disabled={disabled}
              onChange={(e) => updateRow(row.key, { unitDollars: e.target.value })}
            />
            <span className="text-sm text-text-secondary" data-testid="line-item-amount">
              {centsToUsd(amount)}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() => removeRow(row.key)}
              aria-label="Remove line item"
            >
              ✕
            </Button>
          </div>
        );
      })}

      <Button type="button" variant="secondary" size="sm" onClick={onAdd} disabled={disabled}>
        Add line item
      </Button>
    </div>
  );
}
